import { MAX_PROPOSED_ACTIONS_PER_TURN } from "../model/constants"
import type { SofiaProposedAction } from "../model/types"
import {
  isKnownKind,
  isRecord,
  parseRenderVisualizationArgs,
} from "./actionArgumentReaders"
import { isAutoExecute } from "./actionGates"
import { parseAlertActionArgs } from "./alertActionArgs"

/**
 * One raw proposal this build refused, with the reason it was refused.
 *
 * The drop itself is by design (fail closed), but it used to be silent on both
 * sides: Sofia would say "confirm it in the panel" and no card would ever
 * appear, so the agent kept narrating a card that does not exist. Reporting the
 * kind and reason back into the next turn's context lets it re-emit a valid
 * proposal instead. Model-facing only — never rendered to the user.
 */
export interface DroppedProposal {
  /** Raw `kind` from the payload, or "unknown" when absent or not a string. */
  kind: string
  /** Terse reason, e.g. "unknown action kind". */
  reason: string
}

interface ProposedActionsParseResult {
  /** Valid proposals, or null when the payload is absent or nothing survived. */
  actions: SofiaProposedAction[] | null
  dropped: DroppedProposal[]
}

const UNKNOWN_KIND = "unknown"
const OVER_CAP_REASON = `over the ${MAX_PROPOSED_ACTIONS_PER_TURN}-proposal limit for one turn`

type ProposalCheck =
  | { ok: true; action: SofiaProposedAction }
  | { ok: false; reason: string }

function entryKind(entry: Record<string, unknown>): string {
  const { kind } = entry
  return typeof kind === "string" && kind.length > 0 ? kind : UNKNOWN_KIND
}

/** Validate one raw entry into the typed wire shape, or say why it cannot be. */
function checkProposal(
  entry: Record<string, unknown>,
  seen: Set<string>,
): ProposalCheck {
  const {
    kind,
    args,
    rationale,
    proposal_id,
    requires_confirmation,
    action_id,
    expires_at,
  } = entry
  if (!isKnownKind(kind)) {
    return { ok: false, reason: "unknown action kind" }
  }
  if (!isRecord(args)) {
    return { ok: false, reason: "args must be an object" }
  }
  if (typeof rationale !== "string") {
    return { ok: false, reason: "rationale must be a string" }
  }
  if (typeof proposal_id !== "string" || proposal_id.length === 0) {
    return { ok: false, reason: "proposal_id must be a non-empty string" }
  }
  if (typeof requires_confirmation !== "boolean") {
    return { ok: false, reason: "requires_confirmation must be a boolean" }
  }
  // The gate must match the kind: auto-execute kinds (navigate) MUST arrive
  // unconfirmed; every other (write) kind MUST arrive confirmation-gated. A
  // write kind sneaking in with requires_confirmation=false would otherwise
  // auto-drive — fail closed and drop it.
  if (requires_confirmation === isAutoExecute(kind)) {
    return {
      ok: false,
      reason: `requires_confirmation must be ${!isAutoExecute(kind)} for this kind`,
    }
  }
  if (seen.has(proposal_id)) {
    return { ok: false, reason: "duplicate proposal_id" }
  }
  // render_visualization is DEEP-validated here, not just shape-checked: an
  // invalid/over-cap chart spec drops the entry entirely — identical
  // semantics to an unknown kind, so a broken chart card never enters the
  // transcript. Hydration re-validation (utils/storage.ts) routes through
  // this same parser, so persisted specs get re-checked for free.
  if (
    kind === "render_visualization" &&
    parseRenderVisualizationArgs(args) === null
  ) {
    return { ok: false, reason: "invalid visualization spec" }
  }
  const action: SofiaProposedAction = {
    kind,
    args,
    rationale,
    proposal_id,
    requires_confirmation,
    // Captured when present so SERVER_EXECUTED_KINDS can echo the signed
    // tuple back to Sofia. Absent for kinds the FE runs itself.
    action_id: typeof action_id === "string" ? action_id : undefined,
    expires_at: typeof expires_at === "number" ? expires_at : undefined,
  }
  // Validate before rendering or hydrating a card, while preserving its signed
  // arguments verbatim for confirmation. Null is not an alert clearing operation.
  const alertArgs = parseAlertActionArgs(action)
  if (alertArgs?.ok === false) {
    return { ok: false, reason: alertArgs.problems.join("; ") }
  }
  return { ok: true, action }
}

/**
 * Validate a raw `proposed_actions` payload, reporting both what survived and
 * what was refused. The gate is fail-closed: write kinds (launch_run /
 * rerun_run / attach_metrics_to_agent) MUST arrive `requires_confirmation:
 * true`, and auto-execute kinds (`navigate`) MUST arrive `false`.
 */
export function parseProposedActionsWithDrops(
  value: unknown,
): ProposedActionsParseResult {
  if (!Array.isArray(value)) {
    return { actions: null, dropped: [] }
  }
  const seen = new Set<string>()
  const actions: SofiaProposedAction[] = []
  const dropped: DroppedProposal[] = []
  for (const entry of value) {
    if (!isRecord(entry)) {
      dropped.push({ kind: UNKNOWN_KIND, reason: "entry is not an object" })
      continue
    }
    if (actions.length >= MAX_PROPOSED_ACTIONS_PER_TURN) {
      dropped.push({ kind: entryKind(entry), reason: OVER_CAP_REASON })
      continue
    }
    const checked = checkProposal(entry, seen)
    if (!checked.ok) {
      dropped.push({ kind: entryKind(entry), reason: checked.reason })
      continue
    }
    seen.add(checked.action.proposal_id)
    actions.push(checked.action)
  }
  return { actions: actions.length > 0 ? actions : null, dropped }
}

/**
 * Validate a raw `proposed_actions` payload into the typed wire shape. Returns
 * null when the payload is absent or nothing valid remains — callers treat both
 * as "no proposals this turn". Use `parseProposedActionsWithDrops` when the
 * caller also needs to report the refusals back to Sofia.
 */
export function parseProposedActions(
  value: unknown,
): SofiaProposedAction[] | null {
  return parseProposedActionsWithDrops(value).actions
}
